import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Search, Upload, X, AlertTriangle, Check, FileSpreadsheet, ArrowLeftRight, Save, RotateCcw, Plus, Trash2, FileDown } from 'lucide-react';

// Road Mileage (RM) module — a 100%-Excel-faithful replica of the RM workbook.
// The register grid (rm_road) and every abstract sheet (rm_abstract) render with the
// same 3-tier merged header and the same cell values as the source Excel.
const API_BASE = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';
const CURRENT_FY = '2025-26';

const authHeaders = (): Record<string, string> => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};
async function apiGet(path: string): Promise<any> {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}
async function apiSend(method: string, path: string, body?: any): Promise<any> {
  const res = await fetch(`${API_BASE}${path}`, {
    method, headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
  return res.json();
}
const apiPut = (path: string, body: any) => apiSend('PUT', path, body);
const apiPost = (path: string, body: any) => apiSend('POST', path, body);
const apiDelete = (path: string) => apiSend('DELETE', path);

// ============================================================================
// HEADER SPEC — a group is one of:
//   col    : a single column that spans all 3 header rows
//   leaves : a titled band whose leaf labels sit one row below (2 levels)
//   subs   : a titled band split into sub-bands, each with leaf labels (3 levels)
// This reproduces the workbook's merged header exactly (e.g. Surface Details → BT/CC/
// WBM/Unmetalled → per-width).
// ============================================================================
type Cell = 'text' | 'num' | 'int';
type Leaf = { key: string; label: string; type?: Cell; w?: number };
type Sub = { title: string; leaves: Leaf[]; single?: boolean };
type Group =
  | ({ kind: 'col' } & Leaf)
  | { kind: 'leaves'; title: string; leaves: Leaf[] }
  | { kind: 'subs'; title: string; subs: Sub[] };

const leafCount = (g: Group): number =>
  g.kind === 'col' ? 1 : g.kind === 'leaves' ? g.leaves.length : g.subs.reduce((n, s) => n + s.leaves.length, 0);
const flatten = (groups: Group[]): Leaf[] =>
  groups.flatMap((g) => (g.kind === 'col' ? [g] : g.kind === 'leaves' ? g.leaves : g.subs.flatMap((s) => s.leaves)));

const GROUP_COLOR: Record<string, string> = {
  'Length in KM': 'bg-cyan-700', 'Surface Details': 'bg-orange-700', 'CD works': 'bg-rose-700',
  'Bridges & Causeways': 'bg-fuchsia-700', _default: 'bg-slate-700',
};
const bandColor = (t: string) => GROUP_COLOR[t] || GROUP_COLOR._default;

// Renders the three merged header rows for a given spec.
const MergedHeader: React.FC<{ groups: Group[]; leadCols?: string[]; trailCols?: string[] }> = ({ groups, leadCols = [], trailCols = [] }) => (
  <thead className="sticky top-0 z-20">
    {/* Row 1 */}
    <tr>
      {leadCols.map((l, i) => (
        <th key={`l1-${i}`} rowSpan={3} className="sticky left-0 z-30 bg-gray-800 text-white border px-2 py-1.5 text-xs">{l}</th>
      ))}
      {groups.map((g, i) => {
        if (g.kind === 'col')
          return <th key={i} rowSpan={3} style={{ minWidth: g.w }} className="bg-slate-700 text-white border px-2 py-1.5 text-xs font-semibold align-bottom whitespace-nowrap">{g.label}</th>;
        return <th key={i} colSpan={leafCount(g)} rowSpan={g.kind === 'leaves' ? 2 : 1} className={`${bandColor(g.title)} text-white border px-2 py-1.5 text-xs font-semibold text-center whitespace-nowrap`}>{g.title}</th>;
      })}
      {trailCols.map((l, i) => (
        <th key={`t1-${i}`} rowSpan={3} className="sticky right-0 z-30 bg-gray-800 text-white border px-2 py-1.5 text-xs">{l}</th>
      ))}
    </tr>
    {/* Row 2 — sub-band titles for 3-level groups only */}
    <tr>
      {groups.filter((g) => g.kind === 'subs').flatMap((g) =>
        (g as any).subs.map((s: Sub, j: number) =>
          s.single
            ? <th key={`${g.kind}-${g.kind === 'subs' ? (g as any).title : ''}-${j}`} rowSpan={2} className="bg-orange-600 text-white border px-2 py-1.5 text-[11px] font-medium text-center whitespace-nowrap">{s.title}</th>
            : <th key={`s2-${(g as any).title}-${j}`} colSpan={s.leaves.length} className="bg-orange-600 text-white border px-2 py-1 text-[11px] font-medium text-center whitespace-nowrap">{s.title}</th>,
        ),
      )}
    </tr>
    {/* Row 3 — leaf labels */}
    <tr className="bg-gray-100">
      {groups.flatMap((g, gi) => {
        if (g.kind === 'col') return [];
        const leaves = g.kind === 'leaves' ? g.leaves : g.subs.filter((s) => !s.single).flatMap((s) => s.leaves);
        return leaves.map((lf, li) => (
          <th key={`l3-${gi}-${li}`} style={{ minWidth: lf.w }} className="border px-2 py-1 text-[11px] font-medium text-gray-600 text-center whitespace-nowrap">{lf.label}</th>
        ));
      })}
    </tr>
  </thead>
);

// ---- hierarchy filter helpers (client-side, derived from loaded register rows) ----
const nkey = (v: any): string => String(v ?? '').trim().toLowerCase();
// Distinct display values for a column (optionally scoped by a predicate), merged
// case-insensitively so spelling variants (e.g. Suryapet / suryapet) collapse to one.
const distinctBy = (rows: any[], key: string, pred?: (r: any) => boolean): string[] => {
  const m = new Map<string, string>();
  for (const r of rows) {
    if (pred && !pred(r)) continue;
    const v = r[key];
    if (v == null || v === '') continue;
    const k = nkey(v);
    if (!m.has(k)) m.set(k, String(v));
  }
  return Array.from(m.values()).sort((a, b) => a.localeCompare(b));
};

// ---- value formatter: faithful to how Excel shows the number (trim trailing zeros) --
const fmt = (v: any, type?: Cell): string => {
  if (v === null || v === undefined || v === '') return '';
  if (type === 'text') return String(v);
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  if (n === 0) return '';
  return type === 'int' ? String(Math.round(n)) : String(+n.toFixed(5));
};

// ============================================================================
// COLUMN SPECS
// ============================================================================
const WIDTHS = ['3.75 m', '5.50 m', '7.00 m', '> 7.00 m'];
const W = (key: string): Leaf[] => [
  { key: `${key}_w_3_75`, label: WIDTHS[0], type: 'num' }, { key: `${key}_w_5_50`, label: WIDTHS[1], type: 'num' },
  { key: `${key}_w_7_00`, label: WIDTHS[2], type: 'num' }, { key: `${key}_w_above_7`, label: WIDTHS[3], type: 'num' },
];

// Register (rm_road) — PRED work-wise & R&B (identical columns; importer already mapped BT/CC).
const REGISTER_GROUPS: Group[] = [
  { kind: 'col', key: 'source_sl_no', label: 'Sl No', type: 'int', w: 48 },
  { kind: 'col', key: 'district_name', label: 'District', type: 'text', w: 100 },
  { kind: 'col', key: 'constituency_name', label: 'Constituency', type: 'text', w: 110 },
  { kind: 'col', key: 'road_id', label: 'Road ID', type: 'text', w: 82 },
  { kind: 'col', key: 'mandal_name', label: 'Mandal', type: 'text', w: 100 },
  { kind: 'col', key: 'nomenclature', label: 'Nomenclature of the Road', type: 'text', w: 260 },
  { kind: 'leaves', title: 'Length in KM', leaves: [
    { key: 'odr_km', label: 'ODR', type: 'num' }, { key: 'mdr_km', label: 'MDR', type: 'num' },
    { key: 'vr_km', label: 'VR', type: 'num' }, { key: 'class_total_km', label: 'Total', type: 'num' }] },
  { kind: 'col', key: 'start_mileage', label: 'Starting Mileage', type: 'num', w: 80 },
  { kind: 'col', key: 'end_mileage', label: 'Ending Mileage', type: 'num', w: 80 },
  { kind: 'col', key: 'total_length_km', label: 'Total Length of Kms', type: 'num', w: 90 },
  { kind: 'subs', title: 'Surface Details', subs: [
    { title: 'BT', leaves: [...W('bt'), { key: 'bt_total', label: 'Total', type: 'num' }] },
    { title: 'CC', leaves: [...W('cc'), { key: 'cc_total', label: 'Total', type: 'num' }] },
    { title: 'WBM', single: true, leaves: [{ key: 'wbm_km', label: 'WBM', type: 'num' }] },
    { title: 'Unmetalled', leaves: [
      { key: 'unmetalled_motorable', label: 'Motorable', type: 'num' },
      { key: 'unmetalled_unmotorable', label: 'UnMotorable', type: 'num' },
      { key: 'unmetalled_total', label: 'Total', type: 'num' }] },
  ] },
  { kind: 'col', key: 'surface_total_km', label: 'Total Length (BT+CC+WBM+Unmet.)', type: 'num', w: 96 },
  { kind: 'col', key: 'bus_plying_km', label: 'Bus Plying Length', type: 'num', w: 84 },
  { kind: 'leaves', title: 'CD works', leaves: [
    { key: 'cd_pipe_culvert', label: 'Pipe Culvert', type: 'int' }, { key: 'cd_cuddy_stone_culvert', label: 'Cuddy Stone', type: 'int' },
    { key: 'cd_slab_culvert_6m', label: 'Slab ≤6.0m', type: 'int' }, { key: 'cd_road_dam', label: 'Road dam', type: 'int' },
    { key: 'cd_causeway', label: 'Causeway', type: 'int' }] },
  { kind: 'leaves', title: 'Bridges & Causeways', leaves: [
    { key: 'bridge_minor_6_60m', label: 'Minor (6–60 m)', type: 'int' }, { key: 'bridge_major_60m', label: 'Major (> 60 m)', type: 'int' }] },
  { kind: 'col', key: 'remarks', label: 'Remarks', type: 'text', w: 150 },
];

// Abstract measure block (with per-surface "No. of Roads" counts, verbatim from the sheet).
const ABS_FULL: Group[] = [
  { kind: 'col', key: 'no_of_roads', label: 'No. of Roads', type: 'int', w: 70 },
  { kind: 'leaves', title: 'Length in KM', leaves: [
    { key: 'odr_km', label: 'ODR', type: 'num' }, { key: 'mdr_km', label: 'MDR', type: 'num' },
    { key: 'vr_km', label: 'VR', type: 'num' }, { key: 'class_total_km', label: 'Total', type: 'num' }] },
  { kind: 'subs', title: 'Surface Details', subs: [
    { title: 'BT', leaves: [{ key: 'bt_no_of_roads', label: 'No. of Roads', type: 'int' }, ...W('bt'), { key: 'bt_total', label: 'Total', type: 'num' }] },
    { title: 'CC', leaves: [{ key: 'cc_no_of_roads', label: 'No. of Roads', type: 'int' }, ...W('cc'), { key: 'cc_total', label: 'Total', type: 'num' }] },
    { title: 'WBM', leaves: [{ key: 'wbm_no_of_roads', label: 'No. of Roads', type: 'int' }, { key: 'wbm_km', label: 'Length', type: 'num' }] },
    { title: 'Unmetalled', leaves: [
      { key: 'unmet_mot_no_of_roads', label: 'No. (Mot.)', type: 'int' }, { key: 'unmetalled_motorable', label: 'Motorable', type: 'num' },
      { key: 'unmet_unmot_no_of_roads', label: 'No. (Unmot.)', type: 'int' }, { key: 'unmetalled_unmotorable', label: 'UnMotorable', type: 'num' },
      { key: 'unmetalled_total', label: 'Total', type: 'num' }] },
  ] },
  { kind: 'col', key: 'surface_total_km', label: 'Total Length', type: 'num', w: 90 },
  { kind: 'col', key: 'bus_plying_km', label: 'Bus Plying Length', type: 'num', w: 84 },
  { kind: 'leaves', title: 'CD works', leaves: [
    { key: 'cd_pipe_culvert', label: 'Pipe Culvert', type: 'int' }, { key: 'cd_cuddy_stone_culvert', label: 'Cuddy Stone', type: 'int' },
    { key: 'cd_slab_culvert_6m', label: 'Slab ≤6.0m', type: 'int' }, { key: 'cd_road_dam', label: 'Road dam', type: 'int' },
    { key: 'cd_causeway', label: 'Causeway', type: 'int' }] },
  { kind: 'leaves', title: 'Bridges & Causeways', leaves: [
    { key: 'bridge_minor_6_60m', label: 'Minor (6–60 m)', type: 'int' }, { key: 'bridge_major_60m', label: 'Major (> 60 m)', type: 'int' }] },
];
const DCOL: Group = { kind: 'col', key: 'district_name', label: 'District', type: 'text', w: 110 };
const CCOL: Group = { kind: 'col', key: 'constituency_name', label: 'Constituency', type: 'text', w: 120 };
const MCOL: Group = { kind: 'col', key: 'mandal_name', label: 'Mandal', type: 'text', w: 120 };

// Pivot: CC before BT, no per-surface counts, single count column.
const PIVOT_GROUPS: Group[] = [
  DCOL, CCOL, MCOL,
  { kind: 'col', key: 'no_of_roads', label: 'Count', type: 'int', w: 60 },
  { kind: 'leaves', title: 'Length in KM', leaves: [
    { key: 'odr_km', label: 'ODR', type: 'num' }, { key: 'mdr_km', label: 'MDR', type: 'num' },
    { key: 'vr_km', label: 'VR', type: 'num' }, { key: 'class_total_km', label: 'Total', type: 'num' }] },
  { kind: 'subs', title: 'Surface Details', subs: [
    { title: 'CC', leaves: [...W('cc'), { key: 'cc_total', label: 'Total', type: 'num' }] },
    { title: 'BT', leaves: [...W('bt'), { key: 'bt_total', label: 'Total', type: 'num' }] },
    { title: 'WBM', single: true, leaves: [{ key: 'wbm_km', label: 'WBM', type: 'num' }] },
    { title: 'Unmetalled', leaves: [
      { key: 'unmetalled_motorable', label: 'Motorable', type: 'num' }, { key: 'unmetalled_unmotorable', label: 'UnMotorable', type: 'num' },
      { key: 'unmetalled_total', label: 'Total', type: 'num' }] },
  ] },
  { kind: 'col', key: 'surface_total_km', label: 'Total Length', type: 'num', w: 90 },
  { kind: 'col', key: 'bus_plying_km', label: 'Bus Plying Length', type: 'num', w: 84 },
  { kind: 'leaves', title: 'CD works', leaves: [
    { key: 'cd_pipe_culvert', label: 'Pipe Culvert', type: 'int' }, { key: 'cd_cuddy_stone_culvert', label: 'Cuddy Stone', type: 'int' },
    { key: 'cd_slab_culvert_6m', label: 'Slab ≤6.0m', type: 'int' }, { key: 'cd_road_dam', label: 'Road dam', type: 'int' },
    { key: 'cd_causeway', label: 'Causeway', type: 'int' }] },
  { kind: 'leaves', title: 'Bridges & Causeways', leaves: [
    { key: 'bridge_minor_6_60m', label: 'Minor (6–60 m)', type: 'int' }, { key: 'bridge_major_60m', label: 'Major (> 60 m)', type: 'int' }] },
];
const SUMMARY_GROUPS: Group[] = [
  DCOL,
  { kind: 'col', key: 'no_of_roads', label: 'No. of Roads', type: 'int', w: 80 },
  { kind: 'col', key: 'cc_total', label: 'Cement Concrete', type: 'num', w: 110 },
  { kind: 'col', key: 'bt_total', label: 'Black Top', type: 'num', w: 100 },
  { kind: 'col', key: 'wbm_km', label: 'Metalled', type: 'num', w: 100 },
  { kind: 'col', key: 'unmetalled_total', label: 'Un Metalled', type: 'num', w: 100 },
  { kind: 'col', key: 'surface_total_km', label: 'Total', type: 'num', w: 100 },
];

type TabKey = 'REGISTER' | 'SUMMARY' | 'DISTRICT' | 'CONSTITUENCY' | 'MANDAL' | 'PIVOT';
const ABSTRACT_SPEC: Record<Exclude<TabKey, 'REGISTER'>, { grain: string; groups: Group[]; sheet: string }> = {
  SUMMARY: { grain: 'SUMMARY', groups: SUMMARY_GROUPS, sheet: 'ABSTRACT' },
  DISTRICT: { grain: 'DISTRICT', groups: [DCOL, ...ABS_FULL], sheet: 'Dist. Abs' },
  CONSTITUENCY: { grain: 'CONSTITUENCY', groups: [DCOL, CCOL, ...ABS_FULL], sheet: 'Const. Abs.' },
  MANDAL: { grain: 'MANDAL', groups: [DCOL, CCOL, MCOL, ...ABS_FULL], sheet: 'Mandal Abst' },
  PIVOT: { grain: 'PIVOT', groups: PIVOT_GROUPS, sheet: 'Pivot' },
};

const TABS: { key: TabKey; label: string }[] = [
  { key: 'REGISTER', label: 'Work-wise Register' },
  { key: 'SUMMARY', label: 'Abstract (Summary)' },
  { key: 'DISTRICT', label: 'District Abstract' },
  { key: 'CONSTITUENCY', label: 'Constituency Abstract' },
  { key: 'MANDAL', label: 'Mandal Abstract' },
  { key: 'PIVOT', label: 'Pivot' },
];

// ============================================================================
// A faithful data table (header + rows) for a given spec.
// ============================================================================
const FaithfulTable: React.FC<{ groups: Group[]; rows: any[]; numbered?: boolean; grandTotalKey?: string }> = ({ groups, rows, numbered, grandTotalKey }) => {
  const leaves = useMemo(() => flatten(groups), [groups]);
  const totalCols = leaves.length + (numbered ? 1 : 0);
  return (
    <table className="border-collapse text-xs">
      <MergedHeader groups={groups} leadCols={numbered ? ['#'] : []} />
      <tbody>
        {rows.map((r, i) => {
          const isGT = grandTotalKey ? !!r[grandTotalKey] : false;
          return (
            <tr key={r.id ?? i} className={isGT ? 'bg-amber-50 font-semibold' : i % 2 ? 'bg-gray-50' : 'bg-white'}>
              {numbered && <td className="sticky left-0 z-10 border px-2 py-1 text-center text-gray-400 bg-inherit">{isGT ? 'Σ' : i + 1}</td>}
              {leaves.map((lf) => {
                const isText = lf.type === 'text';
                return (
                  <td key={lf.key} style={{ minWidth: lf.w }}
                    className={`border px-2 py-1 ${isText ? 'text-left text-gray-700' : 'text-right tabular-nums text-gray-800'} whitespace-nowrap`}>
                    {fmt(r[lf.key], lf.type)}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {rows.length === 0 && <tr><td colSpan={totalCols} className="px-4 py-10 text-center text-gray-400">No rows</td></tr>}
      </tbody>
    </table>
  );
};

// ============================================================================
// EDITABLE REGISTER GRID — click a cell to edit (uncontrolled input mounts only
// on the active cell, so typing never re-renders the 27k-row grid). Per-row Save
// persists via PUT /rm/:id. Rows are memoized so only the touched row re-renders.
// ============================================================================
const REGISTER_LEAVES = flatten(REGISTER_GROUPS);
// Fixed row height (px) — the register grid is windowed, so every data row must be an
// exact, known height for scroll math. Single-line cells (whitespace-nowrap) fit inside 30px.
const ROW_H = 30;
const REGISTER_NCOLS = REGISTER_LEAVES.length + 2; // leading '#' + trailing 'Action'

type ERow = any & { _dirty?: boolean; _saving?: boolean; _saved?: boolean; _error?: string | null };

const EditableRegisterRow = React.memo(function EditableRegisterRow({
  row, index, editKey, onStart, onCommit, onCancel, onSave, onRevert, onDelete,
}: {
  row: ERow; index: number; editKey: string | null;
  onStart: (id: number, key: string) => void;
  onCommit: (id: number, key: string, value: string) => void;
  onCancel: () => void;
  onSave: (id: number) => void;
  onRevert: (id: number) => void;
  onDelete: (id: number) => void;
}) {
  const isNew = !!row._isNew;
  const rowCls = row._error ? 'bg-red-50' : isNew ? 'bg-amber-50' : row._dirty ? 'bg-blue-50' : index % 2 ? 'bg-gray-50' : 'bg-white';
  return (
    <tr className={rowCls} style={{ height: ROW_H }}>
      <td className="sticky left-0 z-10 border px-2 py-1 text-center text-gray-400 bg-inherit">{isNew ? <span className="text-amber-600 font-semibold">NEW</span> : index + 1}</td>
      {REGISTER_LEAVES.map((lf) => {
        const isText = lf.type === 'text';
        if (editKey === lf.key) {
          return (
            <td key={lf.key} className="border p-0">
              <input
                autoFocus defaultValue={fmt(row[lf.key], lf.type)} type={isText ? 'text' : 'number'} step="any"
                onBlur={(e) => onCommit(row.id, lf.key, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); else if (e.key === 'Escape') onCancel(); }}
                className={`w-full text-xs px-1 py-0.5 border border-blue-400 rounded-sm outline-none ${isText ? 'text-left' : 'text-right'}`}
              />
            </td>
          );
        }
        return (
          <td key={lf.key} style={{ minWidth: lf.w }} onClick={() => onStart(row.id, lf.key)}
            title="Click to edit"
            className={`border px-2 py-1 cursor-pointer hover:bg-blue-50 ${isText ? 'text-left text-gray-700' : 'text-right tabular-nums text-gray-800'} whitespace-nowrap`}>
            {fmt(row[lf.key], lf.type)}
          </td>
        );
      })}
      <td className="sticky right-0 z-10 border px-1 py-1 text-center bg-inherit">
        <div className="flex items-center gap-1 justify-center">
          <button onClick={() => onSave(row.id)} disabled={row._saving || !row._dirty}
            title={row._error || (row._saved ? 'Saved' : 'Save row')}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${row._error ? 'bg-red-100 text-red-700' : row._saved ? 'bg-green-100 text-green-700' : row._dirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-default'}`}>
            {row._saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : row._saved ? <Check className="h-3 w-3" /> : <Save className="h-3 w-3" />}
          </button>
          <button onClick={() => onRevert(row.id)} disabled={row._saving || (!row._dirty && !isNew)}
            title={isNew ? 'Discard this new row' : 'Revert unsaved changes'}
            className={`p-1 rounded ${row._dirty || isNew ? 'text-amber-600 hover:bg-amber-50' : 'text-gray-300 cursor-default'}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onDelete(row.id)} disabled={row._saving}
            title={isNew ? 'Remove this new row' : 'Delete road (soft delete)'}
            className="p-1 rounded text-red-500 hover:bg-red-50">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}, (p, n) => p.row === n.row && p.index === n.index && p.editKey === n.editKey);

// Windowed register grid: only the rows in (and near) the viewport are mounted, so the
// 27k-row register renders instantly instead of trying to build ~1.35M DOM cells at once.
// Off-screen rows are represented by two spacer <tr>s whose heights preserve the scrollbar.
const EditableRegisterTable: React.FC<{
  rows: ERow[];
  scrollParent: React.RefObject<HTMLDivElement | null>;
  editing: { id: number; key: string } | null;
  onStart: (id: number, key: string) => void;
  onCommit: (id: number, key: string, value: string) => void;
  onCancel: () => void;
  onSave: (id: number) => void;
  onRevert: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ rows, scrollParent, editing, onStart, onCommit, onCancel, onSave, onRevert, onDelete }) => {
  const OVERSCAN = 12;
  const tableRef = useRef<HTMLTableElement>(null);
  const [range, setRange] = useState({ start: 0, end: 60 });

  const recompute = useCallback(() => {
    const el = scrollParent.current;
    if (!el) return;
    const headH = tableRef.current?.tHead?.offsetHeight ?? 0; // sticky header sits above the rows
    const viewH = el.clientHeight || 0;
    const perView = Math.ceil(viewH / ROW_H) + OVERSCAN * 2;
    const scrolled = Math.max(0, el.scrollTop - headH);
    const maxStart = Math.max(0, rows.length - Math.ceil(viewH / ROW_H));
    const start = Math.min(Math.max(0, Math.floor(scrolled / ROW_H) - OVERSCAN), maxStart);
    setRange({ start, end: Math.min(rows.length, start + perView) });
  }, [scrollParent, rows.length]);

  useEffect(() => {
    const el = scrollParent.current;
    if (!el) return;
    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => { el.removeEventListener('scroll', recompute); window.removeEventListener('resize', recompute); };
  }, [scrollParent, recompute]);

  const topPad = range.start * ROW_H;
  const botPad = Math.max(0, (rows.length - range.end) * ROW_H);
  return (
    <table ref={tableRef} className="border-collapse text-xs">
      <MergedHeader groups={REGISTER_GROUPS} leadCols={['#']} trailCols={['Action']} />
      <tbody>
        {topPad > 0 && <tr style={{ height: topPad }} aria-hidden><td colSpan={REGISTER_NCOLS} className="p-0" /></tr>}
        {rows.slice(range.start, range.end).map((r, k) => {
          const i = range.start + k;
          return (
            <EditableRegisterRow key={r.id} row={r} index={i}
              editKey={editing && editing.id === r.id ? editing.key : null}
              onStart={onStart} onCommit={onCommit} onCancel={onCancel} onSave={onSave} onRevert={onRevert} onDelete={onDelete} />
          );
        })}
        {botPad > 0 && <tr style={{ height: botPad }} aria-hidden><td colSpan={REGISTER_NCOLS} className="p-0" /></tr>}
        {rows.length === 0 && <tr><td colSpan={REGISTER_NCOLS} className="px-4 py-10 text-center text-gray-400">No rows</td></tr>}
      </tbody>
    </table>
  );
};

// ============================================================================
// IMPORT (validate → commit) — dry-run summary then confirm.
// ============================================================================
const ImportModal: React.FC<{ summary: any; fileName?: string; busy: boolean; onCancel: () => void; onConfirm: () => void }> = ({ summary, fileName, busy, onCancel, onConfirm }) => {
  const u = summary?.unresolved || {};
  const Stat = ({ label, value, color }: any) => (
    <div className={`flex-1 rounded-md border px-3 py-2 text-center ${color}`}><div className="text-lg font-bold">{value}</div><div className="text-[11px] mt-0.5">{label}</div></div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div><h2 className="text-base font-bold text-gray-800">Import preview — dry run</h2>{fileName && <p className="text-xs text-gray-500 truncate max-w-md">{fileName}</p>}</div>
          <button onClick={onCancel} disabled={busy}><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs text-gray-500">Nothing has been written yet. Confirm to replace FY {summary?.financialYear} data.</p>
          <div className="flex gap-2">
            <Stat label="Road rows" value={summary?.roadRows ?? 0} color="bg-blue-50 border-blue-200 text-blue-700" />
            <Stat label="Abstract rows" value={summary?.abstractRows ?? 0} color="bg-emerald-50 border-emerald-200 text-emerald-700" />
            <Stat label="Dropped" value={summary?.droppedRows ?? 0} color="bg-gray-50 border-gray-200 text-gray-600" />
          </div>
          <div className="text-[11px] text-gray-500">
            Per register: {Object.entries(summary?.perRegister || {}).map(([k, v]) => `${k}=${v}`).join('  ')} ·
            Unresolved names: district {u.district ?? 0}, constituency {u.constituency ?? 0}, mandal {u.mandal ?? 0}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
          <button onClick={onCancel} disabled={busy} className="rounded-md border px-3 py-1.5 text-sm bg-white hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} disabled={busy} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
            <Check className={`h-4 w-4 ${busy ? 'animate-pulse' : ''}`} />{busy ? 'Importing…' : `Confirm & import`}
          </button>
        </div>
      </div>
    </div>
  );
};

// Labelled cascading filter dropdown.
const FilterSelect: React.FC<{
  label: string; value: string; options: string[]; placeholder: string; disabled?: boolean; onChange: (v: string) => void;
}> = ({ label, value, options, placeholder, disabled, onChange }) => (
  <label className="flex flex-col gap-0.5">
    <span className="text-[11px] font-medium text-gray-500">{label}</span>
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100 disabled:text-gray-400 min-w-[150px]">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

// ============================================================================
// MAIN
// ============================================================================
const RoadMileageFullFormat: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('REGISTER');
  const [register, setRegister] = useState<'PRED' | 'RB'>('PRED');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<any | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Inline-edit state (register tab only).
  const [editing, setEditing] = useState<{ id: number; key: string } | null>(null);
  const originalById = useRef<Record<number, any>>({});
  const tempIdRef = useRef(-1);
  const dirtyCount = useMemo(() => (tab === 'REGISTER' ? rows.filter((r) => r._dirty).length : 0), [rows, tab]);

  // Hierarchy filters (register tab) — cascade District → Constituency → Mandal.
  const [fDistrict, setFDistrict] = useState('');
  const [fConstituency, setFConstituency] = useState('');
  const [fMandal, setFMandal] = useState('');
  const districtOpts = useMemo(() => distinctBy(rows, 'district_name'), [rows]);
  const constituencyOpts = useMemo(
    () => (fDistrict ? distinctBy(rows, 'constituency_name', (r) => nkey(r.district_name) === nkey(fDistrict)) : []),
    [rows, fDistrict]);
  const mandalOpts = useMemo(
    () => (fDistrict && fConstituency
      ? distinctBy(rows, 'mandal_name', (r) => nkey(r.district_name) === nkey(fDistrict) && nkey(r.constituency_name) === nkey(fConstituency))
      : []),
    [rows, fDistrict, fConstituency]);
  const visibleRows = useMemo(() => {
    if (tab !== 'REGISTER' || (!fDistrict && !fConstituency && !fMandal)) return rows;
    return rows.filter((r) =>
      (!fDistrict || nkey(r.district_name) === nkey(fDistrict)) &&
      (!fConstituency || nkey(r.constituency_name) === nkey(fConstituency)) &&
      (!fMandal || nkey(r.mandal_name) === nkey(fMandal)));
  }, [rows, tab, fDistrict, fConstituency, fMandal]);
  const clearHierarchy = () => { setFDistrict(''); setFConstituency(''); setFMandal(''); };

  const load = useCallback(async () => {
    setLoading(true); setError(null); setEditing(null);
    setFDistrict(''); setFConstituency(''); setFMandal('');
    try {
      if (tab === 'REGISTER') {
        const qs = new URLSearchParams({ register, financialYear: CURRENT_FY, limit: '30000' });
        if (applied) qs.set('search', applied);
        const data = await apiGet(`/rm/full-format?${qs.toString()}`);
        const rr = (data.rows || []) as any[];
        originalById.current = {};
        rr.forEach((r) => { originalById.current[r.id] = { ...r }; });
        setRows(rr); setTotal(data.total ?? rr.length);
      } else {
        const data = await apiGet(`/rm/abstract?grain=${ABSTRACT_SPEC[tab].grain}&financialYear=${CURRENT_FY}`);
        const arr = Array.isArray(data) ? data : [];
        setRows(arr); setTotal(arr.length);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load'); setRows([]);
    } finally { setLoading(false); }
  }, [tab, register, applied]);

  useEffect(() => { load(); }, [load]);
  // Reset the scroll position when the visible set changes, so the windowed grid starts at the top.
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [tab, fDistrict, fConstituency, fMandal, applied, register]);

  // ---- inline edit handlers ----
  const startEdit = useCallback((id: number, key: string) => setEditing({ id, key }), []);
  const cancelEdit = useCallback(() => setEditing(null), []);
  const commitEdit = useCallback((id: number, key: string, value: string) => {
    setRows((rs) => rs.map((r) => {
      if (r.id !== id) return r;
      if (String(r[key] ?? '') === String(value)) return r; // unchanged → keep identity
      return { ...r, [key]: value, _dirty: true, _saved: false, _error: null };
    }));
    setEditing(null);
  }, []);
  const revertRow = useCallback((id: number) => {
    const o = originalById.current[id];
    if (o) setRows((rs) => rs.map((r) => (r.id === id ? { ...o } : r)));
  }, []);
  const saveRow = useCallback(async (id: number) => {
    let target: any;
    setRows((rs) => rs.map((r) => { if (r.id === id) { target = r; return { ...r, _saving: true, _error: null }; } return r; }));
    if (!target) return;
    try {
      const payload: Record<string, any> = {};
      REGISTER_LEAVES.forEach((lf) => {
        let v = target[lf.key];
        if (lf.type !== 'text') v = (v === '' || v == null) ? 0 : Number(v);
        payload[lf.key] = v;
      });
      if (target._isNew) {
        // Create: District + Nomenclature required; server resolves the FK ids.
        if (!String(target.district_name ?? '').trim()) throw new Error('Enter a District');
        if (!String(target.nomenclature ?? '').trim()) throw new Error('Enter the Nomenclature (road name)');
        const created = await apiPost('/rm', { ...payload, register: target.register || register, financial_year: CURRENT_FY });
        originalById.current[created.id] = { ...created };
        // Replace the temp row with the persisted one (real id, resolved ids).
        setRows((rs) => rs.map((r) => (r.id === id ? { ...created, _saved: true } : r)));
        setTotal((t) => t + 1);
      } else {
        await apiPut(`/rm/${id}`, payload);
        originalById.current[id] = { ...target, _dirty: false, _saved: true, _saving: false, _error: null };
        setRows((rs) => rs.map((r) => (r.id === id ? { ...r, _saving: false, _dirty: false, _saved: true, _error: null } : r)));
      }
    } catch (e: any) {
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, _saving: false, _error: e?.message || 'Save failed' } : r)));
    }
  }, [register]);

  const addNewRow = useCallback(() => {
    clearHierarchy(); // ensure the blank row is visible under any active filter
    const id = tempIdRef.current--;
    const blank: any = {
      id, _isNew: true, _dirty: true, register, financial_year: CURRENT_FY,
      district_name: '', constituency_name: '', mandal_name: '', road_id: '', nomenclature: '',
    };
    setRows((rs) => [blank, ...rs]);
  }, [register]);

  const deleteRow = useCallback(async (id: number) => {
    if (id < 0) { // unsaved new row → just drop it
      setRows((rs) => rs.filter((r) => r.id !== id));
      return;
    }
    if (!window.confirm(`Delete road #${id}? It will be removed from the active register (soft delete).`)) return;
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, _saving: true, _error: null } : r)));
    try {
      await apiDelete(`/rm/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      setMsg({ ok: true, text: `Road #${id} deleted.` });
    } catch (e: any) {
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, _saving: false, _error: e?.message || 'Delete failed' } : r)));
    }
  }, []);

  const doValidate = async (file: File) => {
    setImporting(true); setMsg(null); setPreview(null); setPendingFile(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API_BASE}/rm/import/validate?financialYear=${CURRENT_FY}`, { method: 'POST', headers: authHeaders(), body: fd });
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
      setPreview(await res.json()); setPendingFile(file);
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Validation failed' }); }
    finally { setImporting(false); }
  };
  const doCommit = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', pendingFile);
      const res = await fetch(`${API_BASE}/rm/import/commit?financialYear=${CURRENT_FY}`, { method: 'POST', headers: authHeaders(), body: fd });
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
      const r = await res.json();
      setMsg({ ok: true, text: `Imported ${r.roadRows} road rows + ${r.abstractRows} abstract rows (batch ${r.batchId}).` });
      setPreview(null); setPendingFile(null); await load();
    } catch (e: any) { setMsg({ ok: false, text: e?.message || 'Import failed' }); }
    finally { setImporting(false); }
  };

  const spec = tab === 'REGISTER' ? { groups: REGISTER_GROUPS, sheet: register === 'PRED' ? ' work wise (Annexure-I)' : ' R&B - work wise (Annexure-V)' } : ABSTRACT_SPEC[tab];

  return (
    <div className="p-3 md:p-4 bg-gray-50 min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h1 className="text-lg font-bold text-gray-800">Road Mileage — Excel-faithful replica</h1>
          <p className="text-xs text-gray-500">
            Every column & value mirrors the RM workbook. Sheet: <span className="font-mono bg-gray-100 px-1">{spec.sheet}</span> · FY {CURRENT_FY} · {total.toLocaleString()} rows
            {tab === 'REGISTER' && <span className="ml-2 text-gray-400">· click any cell to edit</span>}
            {dirtyCount > 0 && <span className="ml-2 text-blue-600 font-medium">{dirtyCount} unsaved row(s)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === 'REGISTER' && (
            <>
              <button onClick={() => setRegister((r) => (r === 'PRED' ? 'RB' : 'PRED'))} className="flex items-center gap-1 border rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50" title="Toggle PRED / R&B register">
                <ArrowLeftRight className="h-4 w-4" /> {register === 'PRED' ? 'PRED' : 'R&B'}
              </button>
              <div className="relative">
                <Search className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setApplied(search)}
                  placeholder="Search road / district / mandal…" className="border rounded-md pl-8 pr-3 py-1.5 text-sm bg-white w-56" />
              </div>
              <button onClick={() => setApplied(search)} className="border rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">Apply</button>
              <button onClick={addNewRow} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-700">
                <Plus className="h-4 w-4" /> Add New Work
              </button>
            </>
          )}
          <button onClick={load} className="flex items-center gap-1 border rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <a href={`${API_BASE}/rm/import/template`} className="flex items-center gap-1 border rounded-md px-3 py-1.5 text-sm bg-white hover:bg-gray-50" title="Download a blank RM import template (.xlsx)">
            <FileDown className="h-4 w-4" /> Template
          </a>
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
            <Upload className={`h-4 w-4 ${importing ? 'animate-pulse' : ''}`} /> Upload workbook
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void doValidate(f); if (fileRef.current) fileRef.current.value = ''; }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-3">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-t-md border-b-2 ${tab === t.key ? 'border-blue-600 text-blue-700 font-semibold bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <FileSpreadsheet className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'REGISTER' && (
        <div className="flex flex-wrap items-end gap-2 mb-3 bg-white border border-gray-200 rounded-lg p-2.5 shadow-sm">
          <FilterSelect label="District" value={fDistrict} options={districtOpts} placeholder="All Districts"
            onChange={(v) => { setFDistrict(v); setFConstituency(''); setFMandal(''); }} />
          <FilterSelect label="Constituency" value={fConstituency} options={constituencyOpts} disabled={!fDistrict}
            placeholder={fDistrict ? 'All Constituencies' : 'Select District'}
            onChange={(v) => { setFConstituency(v); setFMandal(''); }} />
          <FilterSelect label="Mandal" value={fMandal} options={mandalOpts} disabled={!fConstituency}
            placeholder={fConstituency ? 'All Mandals' : 'Select Constituency'} onChange={setFMandal} />
          {(fDistrict || fConstituency || fMandal) && (
            <button onClick={clearHierarchy} className="flex items-center gap-1 border border-gray-300 rounded-md px-3 py-1.5 text-xs bg-white hover:bg-gray-50">
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
          <span className="text-xs text-gray-500 ml-auto">
            Showing <span className="font-semibold text-gray-700">{visibleRows.length.toLocaleString()}</span> of {rows.length.toLocaleString()} rows
          </span>
        </div>
      )}

      {msg && (
        <div className={`mb-3 rounded-md px-3 py-2 text-sm flex items-center justify-between ${msg.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          <span>{msg.text}</span><button onClick={() => setMsg(null)}><X className="h-4 w-4" /></button>
        </div>
      )}
      {preview && <ImportModal summary={preview} fileName={pendingFile?.name} busy={importing} onCancel={() => { setPreview(null); setPendingFile(null); }} onConfirm={doCommit} />}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 flex items-center justify-between">
          <div className="flex items-center"><AlertTriangle className="h-5 w-5 text-red-400 mr-2" /><span className="text-red-800 text-sm">{error}</span></div>
          <button onClick={load} className="text-red-600 underline text-sm">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><RefreshCw className="h-8 w-8 animate-spin text-blue-500 mr-3" /><span className="text-gray-600">Loading…</span></div>
      ) : (
        <div ref={scrollRef} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto" style={{ maxHeight: 'calc(100vh - 170px)' }}>
          {tab === 'REGISTER' ? (
            <EditableRegisterTable rows={visibleRows} scrollParent={scrollRef} editing={editing}
              onStart={startEdit} onCommit={commitEdit} onCancel={cancelEdit} onSave={saveRow} onRevert={revertRow} onDelete={deleteRow} />
          ) : (
            <FaithfulTable groups={spec.groups} rows={rows} numbered grandTotalKey="is_grand_total" />
          )}
        </div>
      )}
    </div>
  );
};

export default RoadMileageFullFormat;
